import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, ArrowRight, Bell, Award } from "lucide-react";
import WeeklyDigest from "@/components/WeeklyDigest";

const STATUS_LABEL = {
  draft_ready: "Draft ready",
  sent_1: "Message 1 sent",
  sent_2: "Message 2 sent",
  sent_3: "Message 3 sent",
  sent_4: "Message 4 sent",
  resolved: "Resolved",
  failed: "Failed",
};
const STATUS_COLOR = {
  draft_ready: "bg-blue-50 text-blue-700 border-blue-200",
  sent_1: "bg-amber-50 text-amber-700 border-amber-200",
  sent_2: "bg-orange-50 text-orange-700 border-orange-200",
  sent_3: "bg-red-50 text-red-700 border-red-200",
  sent_4: "bg-purple-50 text-purple-700 border-purple-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-slate-50 text-slate-600 border-slate-200",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data)).catch(()=>{}).finally(() => setLoading(false));
    api.get("/reminders").then((r) => setReminders(r.data.reminders || [])).catch(()=>{});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Your cases</h1>
            <p className="text-slate-600 mt-2">Every refund case, all in one place.</p>
          </div>
          <Link to="/case/new" data-testid="dashboard-new-case-button">
            <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-12 px-6">
              <Plus className="w-4 h-4 mr-2" /> New case
            </Button>
          </Link>
        </div>

        {user?.is_founding_100 && (
          <Link to={`/founder/${user.founding_number}`} data-testid="founder-banner-link">
            <Card className="trust-card mb-6 border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 transition-colors">
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 shrink-0 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
                    <Award className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">You're Founder #{user.founding_number}</p>
                    <p className="text-xs text-slate-600 mt-0.5">Grab your shareable founder badge →</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
              </CardContent>
            </Card>
          </Link>
        )}

        <WeeklyDigest />

        {reminders.length > 0 && (
          <Card className="trust-card border-amber-200 bg-amber-50/50 mb-6" data-testid="reminders-banner">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-amber-100 text-amber-700 grid place-items-center">
                  <Bell className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">Time to escalate</p>
                  <p className="text-sm text-slate-600 mt-1">
                    {reminders.length === 1
                      ? `It's been ${reminders[0].hours_since_sent}h on ${reminders[0].merchant_name}. Time to send Message ${reminders[0].next_message_num}.`
                      : `${reminders.length} cases are ready for their next message.`}
                  </p>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {reminders.slice(0, 3).map((r) => (
                      <Link key={r.case_id} to={`/case/${r.case_id}`} data-testid={`reminder-link-${r.case_id}`}>
                        <Button size="sm" variant="outline" className="rounded-full border-amber-300 text-amber-800 bg-white hover:bg-amber-100">
                          Send Message {r.next_message_num} → {r.merchant_name}
                        </Button>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <p className="text-slate-500">Loading…</p>
        ) : cases.length === 0 ? (
          <Card className="trust-card">
            <CardContent className="p-12 text-center">
              <h3 className="text-xl font-semibold text-slate-900">No cases yet</h3>
              <p className="text-slate-600 mt-2">Start your first refund case in under 90 seconds.</p>
              <Link to="/case/new" data-testid="empty-state-start-case-button">
                <Button className="mt-6 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF]">Start your first case</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="cases-list">
            {cases.map((c) => (
              <Link key={c.id} to={`/case/${c.id}`} data-testid={`case-row-${c.id}`}>
                <Card className="trust-card hover:border-[#93C5FD] transition-colors">
                  <CardContent className="p-5 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{c.merchant_name}</p>
                      <p className="text-sm text-slate-500 truncate">
                        ${c.amount?.toFixed(2)} · charged {c.charge_date}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant="outline" className={`${STATUS_COLOR[c.status] || ""} rounded-full`}>
                        {STATUS_LABEL[c.status] || c.status}
                      </Badge>
                      {!c.paid && <Badge variant="outline" className="rounded-full bg-slate-50 text-slate-600 border-slate-200">Unpaid</Badge>}
                      <ArrowRight className="w-4 h-4 text-slate-400 hidden sm:block" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
